'use client';

import { useEffect, useRef } from 'react';
import { useRouter, usePathname, useSearchParams } from 'next/navigation';
import { useAuthStore, useUser } from '@/features/auth';
import { isSafeRedirectPath } from '@/features/auth/lib/safe-redirect';
import { LanguageSelector } from '@/features/i18n/ui/LanguageSelector';
import { AuthTokenService } from '@/shared/lib/auth-token-service';
import {
  AUTH_GATE_ATTRIBUTE,
  GATED_AUTH_PATHS,
} from '@/shared/lib/auth-gate-script';
import { createLogger } from '@/shared/lib/logger';

const logger = createLogger('AuthLayout');

// Auth pages this layout must never navigate away from: /auth/reset-password
// stays reachable while logged in, and the OAuth callbacks exchange the code,
// authenticate, then resolve their own destination — bouncing them to '/'
// mid-exchange raced their own redirect into a second full page load.
const BYPASS_AUTH_REDIRECT_PATHS = [
  '/auth/reset-password',
  '/auth/google/callback',
  '/auth/apple/callback',
  '/auth/vk/callback',
];

export default function AuthLayout({
  children,
}: {
  children: React.ReactNode;
}) {
  const user = useUser();
  const loading = useAuthStore(
    (state) => state.isLoggingIn || state.isRegistering
  );
  const isInitialized = useAuthStore((state) => state.isInitialized);
  const isAuthenticated = useAuthStore((state) => state.isAuthenticated);
  const pendingEmailVerification = useAuthStore(
    (state) => state.pendingEmailVerification
  );
  const router = useRouter();
  const pathname = usePathname();
  const searchParams = useSearchParams();

  const shouldBypassRedirect = BYPASS_AUTH_REDIRECT_PATHS.some((path) =>
    pathname.startsWith(path)
  );

  // A landing CTA deep-links into the app THROUGH login
  // (/auth/login?redirect=/account/agent-keys, and the same for the pricing
  // CTA), and the protected layout sends anonymous visitors here the same way.
  // When the visitor turns out to be signed in already, LoginForm never runs,
  // so this file is the only place left that can honour the parameter —
  // replacing to '/' unconditionally dropped the destination and stranded the
  // visitor on the dashboard. An /auth/* target is refused so a stale link
  // cannot bounce between auth pages.
  const redirectParam = searchParams.get('redirect');
  const authenticatedDestination =
    isSafeRedirectPath(redirectParam) && !redirectParam.startsWith('/auth/')
      ? redirectParam
      : '/';

  // Login is the only auth page on the teal (bg-primary) background — and only
  // in LIGHT theme; dark renders it on `bg-background` like every other auth
  // page (see app/auth/login/page.tsx), so the translucent-white trigger below
  // carries `dark:` fallbacks back to the default select styling.
  const isTealAuthBackground = pathname.startsWith('/auth/login');

  // WHO owns the post-auth navigation. This layout must bounce a visitor who
  // ALREADY had a session when the page opened (a stale /auth/login link, the
  // pricing CTA of a signed-in visitor). It must NOT bounce someone who signs
  // in right here: the forms resolve the real destination (deep link →
  // starter building → solo building) with a network call, and `loading` goes
  // false the moment the credentials clear, BEFORE that call returns. This
  // effect used to fire in exactly that gap and `replace('/')` out from under
  // the form; the root then re-resolved the building on its own. That double
  // navigation is the flicker users saw on every sign-in.
  //
  // An auth mutation observed on this mount means a form owns the navigation
  // for the rest of the mount — the page unmounts as soon as it navigates.
  const authFlowRanHere = useRef(false);
  if (loading) {
    authFlowRanHere.current = true;
  }

  // Did this visitor ALREADY hold a session when the page opened? Read
  // synchronously from storage, once, at mount — the same snapshot the
  // pre-hydration gate script used, and the same trick the `/` resolver uses.
  //
  // This is what makes the bounce immediate. `isInitialized` means "the server
  // confirmed the token", which costs a round trip; waiting for it is what let
  // the login form sit interactive for seconds under a signed-in visitor
  // arriving from a landing CTA. Reading at mount (never re-reading) also
  // keeps a sign-in that happens HERE out of this path: the token that a form
  // stores is written after this ref is filled, so the form keeps ownership of
  // its own navigation, exactly as the comment above requires.
  const hadSessionOnMount = useRef<boolean | null>(null);
  if (hadSessionOnMount.current === null) {
    hadSessionOnMount.current =
      typeof window !== 'undefined' && !!AuthTokenService.getTokenSync();
  }

  // Scoped to the pages a signed-in visitor has no business on — the same list
  // the pre-hydration gate hides. It must NOT cover /auth/verify-email: that
  // page is where a signed-in-but-unconfirmed account belongs, and bouncing it
  // to '/' only for the root to send it straight back is an endless loop, not
  // a redirect. `pendingEmailVerification` guards the same case once the
  // server has answered and the flag survives a soft navigation.
  const optimisticRedirect =
    !!hadSessionOnMount.current &&
    !authFlowRanHere.current &&
    !shouldBypassRedirect &&
    !pendingEmailVerification &&
    GATED_AUTH_PATHS.includes(pathname);

  useEffect(() => {
    // Fast path: a session that predates this mount. No store state is needed
    // — a stale/revoked token is caught downstream (the destination 401s,
    // AuthErrorHandler clears it and sends the visitor back here, this time
    // with nothing in storage), so there is no bounce loop to guard against.
    if (optimisticRedirect) {
      router.replace(authenticatedDestination);
      return;
    }

    // Only redirect after auth is fully initialized and not loading
    // Don't redirect if email verification is pending
    // Don't redirect on pages that should remain accessible when logged in
    if (
      isInitialized &&
      !loading &&
      user &&
      isAuthenticated &&
      !authFlowRanHere.current &&
      !pendingEmailVerification &&
      !shouldBypassRedirect
    ) {
      logger.info('User authenticated, redirecting', {
        destination: authenticatedDestination,
      });
      router.replace(authenticatedDestination);
    }
  }, [
    user,
    loading,
    router,
    isInitialized,
    isAuthenticated,
    pendingEmailVerification,
    shouldBypassRedirect,
    authenticatedDestination,
    optimisticRedirect,
  ]);

  // Hand the pre-hydration gate back. It hid the page on a token alone; only
  // now, with the store settled, do we know whether that token was real. On a
  // stale token this reveals the login form in place (no reload); on a live
  // one the attribute is dropped as this layout unmounts into the app.
  useEffect(() => {
    if (typeof document === 'undefined') return;
    if (!optimisticRedirect && (isInitialized || !hadSessionOnMount.current)) {
      document.documentElement.removeAttribute(AUTH_GATE_ATTRIBUTE);
    }
    return () => {
      document.documentElement.removeAttribute(AUTH_GATE_ATTRIBUTE);
    };
  }, [optimisticRedirect, isInitialized]);

  // Full-screen overlay over an authenticated auth page, instead of an early
  // return that unmounts it. Deliberately NOT gated on `authFlowRanHere`: a
  // just-signed-in user is authenticated while their form still resolves the
  // destination, and the overlay is what covers that window — without it the
  // idle login form stares back for the length of one request.
  const isRedirectingAuthenticated =
    optimisticRedirect ||
    (isInitialized &&
      !!user &&
      isAuthenticated &&
      !pendingEmailVerification &&
      !shouldBypassRedirect);

  // IMPORTANT: children must always render. `isInitialized` is false during
  // SSR (auth lives in localStorage), so an early `return null` here made
  // every /auth/* page ship an EMPTY server-rendered <body>. Bots that don't
  // execute JS (Paddle domain verification, search engines) then saw a blank
  // page and Paddle rejected the domain as "offline or under construction".
  // Auth gating happens after hydration via the redirect effect + overlay.
  return (
    <>
      <div className="fixed top-4 end-4 z-50 w-44">
        <LanguageSelector
          triggerClassName={
            isTealAuthBackground
              ? 'bg-white/15 hover:bg-white/25 border-white/30 text-white backdrop-blur-sm shadow-none transition-colors dark:bg-field dark:hover:bg-field dark:border-input dark:text-foreground dark:backdrop-blur-none'
              : undefined
          }
        />
      </div>
      {children}
      {isRedirectingAuthenticated && (
        <div className="fixed inset-0 z-[60] flex items-center justify-center bg-background">
          <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary"></div>
        </div>
      )}
    </>
  );
}
